import { createContext, useContext, useEffect, useState } from "react";
import { AuthContext } from "./authContext";
import { useParams } from "react-router-dom";
import { API_BASE_URL, API_KEY } from "../config/config";
import axios from "axios";

const MemberContext = createContext(null);

const MemberProvider = ({ children }) => {
	const { visitor } = useContext(AuthContext);
	const { usernameId } = useParams();

	const [userLoading, setUserLoading] = useState(false);
	const [user, setUser] = useState(null);
	const [userId, setUserId] = useState(null);

	const [userData, setUserData] = useState({
		following: false,
	});

	useEffect(() => {
		if (usernameId) {
			const urlPattern = /^([\w-]+)\.(\d+)$/;
			const match = usernameId.match(urlPattern);
			if (match) {
				const [, , matchedUserId] = match;
				setUserId(Number(matchedUserId));
			}
		}
	}, [usernameId]);

	useEffect(() => {
		if (visitor && userId) {
			fetchUser();
		}
	}, [visitor, userId]);

	const fetchUser = async () => {
		if (!visitor || !userId) return;

		if (user !== null && userId === user.user_id) return; // Already loaded

		if (userLoading) return; // Loading

		setPromoteThreadsInitialLoaded(false);
		setPromoteThreads([]);
		setPromoteThreadsPage(1);
		setHasMorePromoteThreads(true);

		setLockedThreadsInitialLoaded(false);
		setLockedThreads([]);
		setLockedThreadsPage(1);
		setHasMoreLockedThreads(true);

		setFollowersInitialLoaded(false);
		setFollowers([]);
		setFollowersPage(1);
		setHasMoreFollowers(true);

		setBadges([]);

		try {
			setUserLoading(true);

			const config = {
				method: "get",
				maxBodyLength: Infinity,
				url: `${API_BASE_URL}/users/${userId}`,
				headers: {
					"XF-Api-Key": API_KEY,
					"XF-Api-User": visitor.user_id,
				},
				withCredentials: true,
			};

			const response = await axios.request(config);
			const fetchedUser = response.data.user;
			setUser(fetchedUser);

			setUserData((prevData) => ({
				...prevData,
				following: fetchedUser.is_followed,
			}));

			setUserLoading(false);

			await fetchPromoteThreads(true);
			await fetchLockedThreads(true);
			await fetchUserFollowers(true);
			await fetchUserBadges();
		} catch (error) {
			console.error("Failed to fetch user:", error);
			setUser(null);
			setUserLoading(false);
		}
	};

	const toggleFollowing = async () => {
		if (!visitor || !userId) return;

		try {
			const config = {
				method: "post",
				maxBodyLength: Infinity,
				url: `${API_BASE_URL}/users/${userId}/follow`,
				headers: {
					"XF-Api-Key": API_KEY,
					"XF-Api-User": visitor.user_id,
				},
				withCredentials: true,
			};

			const response = await axios.request(config);
			const isFollowing = response.data.following;

			setUserData((prevData) => ({
				...prevData,
				following: isFollowing,
			}));
		} catch (error) {
			console.error("Failed to toggle following:", error);
		}
	};

	const [followersInitialLoaded, setFollowersInitialLoaded] = useState(false);
	const [followers, setFollowers] = useState([]);
	const [followersLoading, setFollowersLoading] = useState(false);
	const [followersPage, setFollowersPage] = useState(1);
	const [hasMoreFollowers, setHasMoreFollowers] = useState(true);

	const fetchUserFollowers = async (isInitialLoad = false) => {
		if (!visitor || !userId) return;

		if (isInitialLoad && followersInitialLoaded) return; // Already loaded

		if (followersLoading) return; // Loading

		try {
			setFollowersLoading(true);

			const config = {
				method: "get",
				maxBodyLength: Infinity,
				url: `${API_BASE_URL}/users/${userId}/followers?page=${followersPage}`,
				headers: {
					"XF-Api-Key": API_KEY,
					"XF-Api-User": visitor.user_id,
				},
				withCredentials: true,
			};

			const response = await axios.request(config);
			let newFollowers = response.data.followers;
			if (typeof newFollowers === "object") {
				newFollowers = Object.values(newFollowers);
			}

			setFollowers((prevFollowers) => [...prevFollowers, ...newFollowers]);

			const pagination = response.data.pagination;

			setHasMoreFollowers(pagination.current_page < pagination.last_page);
			setFollowersPage((prevPage) => prevPage + 1);
			setFollowersLoading(false);
			setFollowersInitialLoaded(true);
		} catch (error) {
			console.error("Failed to fetch followers:", error);
			setFollowersLoading(false);
			setFollowersInitialLoaded(true);
		}
	};

	const loadMoreFollowers = () => {
		if (!hasMoreFollowers) return;

		fetchUserFollowers(false);
	};

	const [trophiesLoading, setTrophiesLoading] = useState(false);
	const [trophies, setTrophies] = useState(null);

	const fetchUserTrophies = async () => {
		if (!visitor || !userId) return;

		if (trophies !== null) return; // Already loaded

		if (trophiesLoading) return; // Loading

		try {
			setTrophiesLoading(true);

			const config = {
				method: "get",
				maxBodyLength: Infinity,
				url: `${API_BASE_URL}/users/${userId}/trophies`,
				headers: {
					"XF-Api-Key": API_KEY,
					"XF-Api-User": visitor.user_id,
				},
				withCredentials: true,
			};

			const response = await axios.request(config);
			let fetchedTrophies = response.data.trophies;
			if (typeof fetchedTrophies === "object") {
				fetchedTrophies = Object.values(fetchedTrophies);
			}
			setTrophies(fetchedTrophies);
			setTrophiesLoading(false);
		} catch (error) {
			console.error("Failed to fetch user trophies:", error);
			setTrophies([]);
			setTrophiesLoading(false);
		}
	};

	const [badgesLoading, setBadgesLoading] = useState(false);
	const [badges, setBadges] = useState(null);

	const fetchUserBadges = async () => {
		if (!visitor || !userId) return;

		if (badges !== null) return; // Already loaded

		if (badgesLoading) return; // Loading

		try {
			setBadgesLoading(true);

			const config = {
				method: "get",
				maxBodyLength: Infinity,
				url: `${API_BASE_URL}/users/${userId}/badges`,
				headers: {
					"XF-Api-Key": API_KEY,
					"XF-Api-User": visitor.user_id,
				},
				withCredentials: true,
			};

			const response = await axios.request(config);
			let fetchedBadges = response.data.badges;
			if (typeof fetchedBadges === "object") {
				fetchedBadges = Object.values(fetchedBadges);
			}
			setBadges(fetchedBadges);
			setBadgesLoading(false);
		} catch (error) {
			console.error("Failed to fetch user badges:", error);
			setBadges([]);
			setBadgesLoading(false);
		}
	};

	const [promoteThreadsInitialLoaded, setPromoteThreadsInitialLoaded] = useState(false);
	const [promoteThreads, setPromoteThreads] = useState([]);
	const [promoteThreadsLoading, setPromoteThreadsLoading] = useState(false);
	const [promoteThreadsPage, setPromoteThreadsPage] = useState(1);
	const [hasMorePromoteThreads, setHasMorePromoteThreads] = useState(true);

	const fetchPromoteThreads = async (isInitialLoad = false) => {
		if (!visitor || !userId) return;

		if (isInitialLoad && promoteThreadsInitialLoaded) return; // Already loaded

		if (promoteThreadsLoading) return; // Loading

		try {
			setPromoteThreadsLoading(true);

			const config = {
				method: "get",
				maxBodyLength: Infinity,
				url: `${API_BASE_URL}/users/${userId}/promote-threads?page=${promoteThreadsPage}`,
				headers: {
					"XF-Api-Key": API_KEY,
					"XF-Api-User": visitor.user_id,
				},
				withCredentials: true,
			};

			const response = await axios.request(config);
			let newThreads = response.data.threads;
			if (typeof newThreads === "object") {
				newThreads = Object.values(newThreads);
			}

			setPromoteThreads((prevThreads) => [...prevThreads, ...newThreads]);

			const pagination = response.data.pagination;

			setHasMorePromoteThreads(pagination.current_page < pagination.last_page);
			setPromoteThreadsPage((prevPage) => prevPage + 1);
			setPromoteThreadsLoading(false);
			setPromoteThreadsInitialLoaded(true);
		} catch (error) {
			console.error("Failed to fetch user promote threads:", error);
			setPromoteThreadsLoading(false);
			setPromoteThreadsInitialLoaded(true);
		}
	};

	const loadMorePromoteThreads = () => {
		if (!hasMorePromoteThreads) return;

		fetchPromoteThreads(false);
	};

	const [lockedThreadsInitialLoaded, setLockedThreadsInitialLoaded] = useState(false);
	const [lockedThreads, setLockedThreads] = useState([]);
	const [lockedThreadsLoading, setLockedThreadsLoading] = useState(false);
	const [lockedThreadsPage, setLockedThreadsPage] = useState(1);
	const [hasMoreLockedThreads, setHasMoreLockedThreads] = useState(true);

	const fetchLockedThreads = async (isInitialLoad = false) => {
		if (!visitor || !userId) return;

		if (isInitialLoad && lockedThreadsInitialLoaded) return; // Already loaded

		if (lockedThreadsLoading) return; // Loading

		try {
			setLockedThreadsLoading(true);

			const config = {
				method: "get",
				maxBodyLength: Infinity,
				url: `${API_BASE_URL}/users/${userId}/locked-threads?page=${lockedThreadsPage}`,
				headers: {
					"XF-Api-Key": API_KEY,
					"XF-Api-User": visitor.user_id,
				},
				withCredentials: true,
			};

			const response = await axios.request(config);
			let newThreads = response.data.threads;
			if (typeof newThreads === "object") {
				newThreads = Object.values(newThreads);
			}

			setLockedThreads((prevThreads) => [...prevThreads, ...newThreads]);

			const pagination = response.data.pagination;

			setHasMoreLockedThreads(pagination.current_page < pagination.last_page);
			setLockedThreadsPage((prevPage) => prevPage + 1);
			setLockedThreadsLoading(false);
			setLockedThreadsInitialLoaded(true);
		} catch (error) {
			console.error("Failed to fetch user locked threads:", error);
			setLockedThreadsLoading(false);
			setLockedThreadsInitialLoaded(true);
		}
	};

	const loadMoreLockedThreads = () => {
		if (!hasMoreLockedThreads) return;

		fetchLockedThreads(false);
	};

	return (
		<MemberContext.Provider
			value={{
				user,
				userData,
				toggleFollowing,
				followers,
				followersLoading,
				followersPage,
				hasMoreFollowers,
				loadMoreFollowers,
				fetchUserFollowers,
				trophies,
				fetchUserTrophies,
				badges,
				fetchUserBadges,
				promoteThreads,
				promoteThreadsLoading,
				promoteThreadsPage,
				hasMorePromoteThreads,
				loadMorePromoteThreads,
				fetchPromoteThreads,
				lockedThreads,
				lockedThreadsLoading,
				lockedThreadsPage,
				hasMoreLockedThreads,
				loadMoreLockedThreads,
				fetchLockedThreads,
			}}
		>
			{children}
		</MemberContext.Provider>
	);
};

export { MemberContext, MemberProvider };
